import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  QualifyBidMutation,
  QualifyBidMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../../useGraphQlClient";

const qualifyBid = graphql(`
  mutation QualifyBid($id: String!) {
    qualifyBid(id: $id) {
      result {
        groupName
        questions {
          answerId
          autoQualify
          title
          order
          answer {
            answer
          }
          sources {
            document
            documentId
            chunkNumber
            locations {
              pageNumber
              bbox {
                x
                y
                width
                height
              }
            }
          }
        }
      }
      creditInfo {
        totalCredits
        consumedAllCredits
      }
    }
  }
`);

export function useQualifyBid(
  options?: UseMutationOptions<QualifyBidMutation, unknown, QualifyBidMutationVariables, unknown>,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();
  const { data, ...rest } = useGraphQlMutation(qualifyBid, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      options?.onSuccess?.(data, vars, ctx);
      //  Update the cache so the getBidReport query fetch uses it
      queryClient.setQueryData(["get_bid_report", vars.id], () => {
        return {
          getBidReport: {
            result: data.qualifyBid.result,
          },
        };
      });

      void queryClient.invalidateQueries(["remainingCredits", "BID"]);

      // Only triggers once when a user has consumed all of their credits
      if (data.qualifyBid.creditInfo?.consumedAllCredits) {
        logEvent(EventNames.creditLimitReached, {
          "Data type": "BIDS",
          "Total credits": data.qualifyBid.creditInfo.totalCredits,
        });
      }
    },
  });

  return { data: data?.qualifyBid, ...rest };
}
